import * as THREE from 'three';
import { Object3D } from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { ConstantsOpenPose } from '../constants_open_pose';
import { EOpenPoseBodyBone } from '../enums/e_open_pose_body_bone';
import { EOpenPoseBodyGroup } from '../enums/e_open_pose_body_group';
import { EOpenPoseBodyNode as EOPBN } from '../enums/e_open_pose_body_node';
import { EOpenPoseBodySide } from '../enums/e_open_pose_body_side';
import { EThreejsStub } from '../enums/e_threejs_stub';
import { OpenPoseLimbs } from '../open_pose_types/open_pose_limbs';
import { OpenPoseSelectedNodeInfo } from '../open_pose_types/open_pose_selected_node_info';
import { OpenPoseBodyController } from './open_pose_body_controller';
import { is_bone, is_mask, is_pickable } from './utils/open_pose_body_parts';

function get_preset_color_of_joint(name: string) {
  const index = ConstantsOpenPose.OPEN_POSE_KEYPOINTS_LIST.indexOf(name);

  return index !== -1 ? to_hex_color(ConstantsOpenPose.CONNECTS_COLORS[index]) : 0x0;
}

export function update_joint_sphere(object_3d: Object3D, thickness = ConstantsOpenPose.BONE_THICKNESS) {
  const name = object_3d.name + ConstantsOpenPose.JOINT_SPHERE_NAME;
  const sphere = object_3d.getObjectByName(name);

  if (!sphere) return;

  sphere.scale.setScalar(thickness / ConstantsOpenPose.BONE_THICKNESS);
}

export function create_or_update_bone_link_cylinder({
  start_object,
  end_object,
  start_label_name,
  end_label_name,
  thickness = ConstantsOpenPose.BONE_THICKNESS,
  is_create = false,
  body_controller,
  use_stored_distance = false,
}: {
  start_object: Object3D;
  end_object: Object3D;
  start_label_name: string;
  end_label_name: string;
  thickness?: number;
  is_create?: boolean;
  body_controller?: OpenPoseBodyController;
  use_stored_distance?: boolean;
}) {
  // Force update matrices to get accurate positions after node movement
  start_object.updateMatrixWorld(true);
  end_object.updateMatrixWorld(true);
  
  // compute end position in start_object local space to support arbitrary hierarchy
  const start_world = new THREE.Vector3();
  const end_world = new THREE.Vector3();
  const end_local_in_start = new THREE.Vector3();

  start_object.getWorldPosition(start_world);
  end_object.getWorldPosition(end_world);

  end_local_in_start.copy(end_world);
  start_object.worldToLocal(end_local_in_start);

  const position_start_local = new THREE.Vector3(0, 0, 0);
  const position_end_local = end_local_in_start;

  const link_name = `${start_label_name}${ConstantsOpenPose.LINK_NAME}${end_label_name}`;
  
  // console.log(`[BONE_POSITIONS] ${link_name}: start_world=${start_world.x.toFixed(2)}, ${start_world.y.toFixed(2)}, ${start_world.z.toFixed(2)}`);
  // console.log(`[BONE_POSITIONS] ${link_name}: end_world=${end_world.x.toFixed(2)}, ${end_world.y.toFixed(2)}, ${end_world.z.toFixed(2)}`);
  
  // Calculate actual distance
  const actual_distance = position_start_local.distanceTo(position_end_local);
  
  // Simple distance logic: use stored distance only in fixed length mode
  let distance = actual_distance;
  if (use_stored_distance && body_controller) {
    const stored_bone = body_controller.get_bones()[link_name];
    if (stored_bone) {
      // Fixed length mode: use stored distance from bone object
      distance = stored_bone.get_distance();
      // console.log(`[CREATE_BONE_CYLINDER] Fixed length mode - using stored distance for ${link_name}: ${distance} (actual: ${actual_distance})`);
    } else {
      // Fixed length mode but no stored bone found, use actual distance
      // console.log(`[CREATE_BONE_CYLINDER] Fixed length mode - no stored bone found for ${link_name}, using actual distance: ${distance}`);
    }
  } else {
    // Float length mode: always use actual distance between nodes
    // console.log(`[CREATE_BONE_CYLINDER] Float length mode - using actual distance for ${link_name}: ${distance}`);
  }
  
  if (distance === 0) return; // avoid degenerate cylinders

  let mesh = start_object.getObjectByName(link_name) as THREE.Mesh;

  if (is_create && !mesh) {
    const preset_color = get_color_of_link_by_name(start_label_name, end_label_name);
    const material = new THREE.MeshBasicMaterial({ color: preset_color ?? 0x0, transparent: true });

    const geometry = new THREE.CylinderGeometry(
      thickness * ConstantsOpenPose.BONE_THICKNESS_MULTIPLIER,
      thickness * ConstantsOpenPose.BONE_THICKNESS_MULTIPLIER,
      distance,
      8,
    );

    mesh = new THREE.Mesh(geometry, material);
    mesh.name = link_name;
    start_object.add(mesh);
  } else if (mesh) {
    const old_geometry = mesh.geometry;
    const geometry = new THREE.CylinderGeometry(
      thickness * ConstantsOpenPose.BONE_THICKNESS_MULTIPLIER,
      thickness * ConstantsOpenPose.BONE_THICKNESS_MULTIPLIER,
      distance,
      8,
    );
    mesh.geometry = geometry;
    mesh.updateMatrixWorld(true);
    old_geometry.dispose();
  } else {
    // nothing to update
    return;
  }

  // place at local midpoint
  const midpoint_local = position_start_local.clone().add(position_end_local).multiplyScalar(0.5);
  mesh.position.copy(midpoint_local);

  // orient cylinder from +Y to the direction vector
  const direction_local = position_end_local.clone().sub(position_start_local).normalize();
  const up_local = new THREE.Vector3(0, 1, 0);
  
  // console.log(`[BONE_DIRECTION] ${link_name}: start_local=${position_start_local.x.toFixed(2)}, ${position_start_local.y.toFixed(2)}, ${position_start_local.z.toFixed(2)}`);
  // console.log(`[BONE_DIRECTION] ${link_name}: end_local=${position_end_local.x.toFixed(2)}, ${position_end_local.y.toFixed(2)}, ${position_end_local.z.toFixed(2)}`);
  // console.log(`[BONE_DIRECTION] ${link_name}: direction=${direction_local.x.toFixed(2)}, ${direction_local.y.toFixed(2)}, ${direction_local.z.toFixed(2)}`);

  const dot = up_local.dot(direction_local);
  if (Math.abs(dot) < 0.9999) {
    const axis = up_local.clone().cross(direction_local).normalize();
    const angle = Math.acos(Math.max(-1, Math.min(1, up_local.dot(direction_local))));
    mesh.setRotationFromAxisAngle(axis, angle);
    // console.log(`[BONE_DIRECTION] ${link_name}: using axis rotation, angle=${(angle * 180 / Math.PI).toFixed(2)}°`);
  } else {
    direction_local.y < 0 ? mesh.rotation.set(Math.PI, 0, 0) : mesh.rotation.set(0, 0, 0);
    // console.log(`[BONE_DIRECTION] ${link_name}: using direct rotation, direction.y=${direction_local.y.toFixed(2)}`);
  }

  // update joint spheres on both ends
  update_joint_sphere(start_object, thickness * 2);
  update_joint_sphere(end_object, thickness * 2);
}

export function create_or_update_bone_link_cylinder_with_fixed_distance({
  start_object,
  end_object,
  start_label_name,
  end_label_name,
  thickness = ConstantsOpenPose.BONE_THICKNESS,
  is_create = false,
  fixed_distance,
}: {
  start_object: Object3D;
  end_object: Object3D;
  start_label_name: string;
  end_label_name: string;
  thickness?: number;
  is_create?: boolean;
  fixed_distance?: number;
}) {
  // compute end position in start_object local space to support arbitrary hierarchy
  const start_world = new THREE.Vector3();
  const end_world = new THREE.Vector3();
  const end_local_in_start = new THREE.Vector3();

  start_object.getWorldPosition(start_world);
  end_object.getWorldPosition(end_world);

  end_local_in_start.copy(end_world);
  start_object.worldToLocal(end_local_in_start);

  const position_start_local = new THREE.Vector3(0, 0, 0);
  const position_end_local = end_local_in_start;

  // Use fixed distance if provided, otherwise compute from positions
  let distance = fixed_distance;
  if (distance === undefined) {
    distance = position_start_local.distanceTo(position_end_local);
  }
  
  if (distance === 0) return; // avoid degenerate cylinders

  const link_name = `${start_label_name}${ConstantsOpenPose.LINK_NAME}${end_label_name}`;
  console.log(`[CREATE_BONE_CYLINDER_FIXED] Creating/updating ${link_name}, fixed_distance: ${fixed_distance}, actual_distance: ${position_start_local.distanceTo(position_end_local)}, using: ${distance}`);
  let mesh = start_object.getObjectByName(link_name) as THREE.Mesh;

  if (is_create && !mesh) {
    const preset_color = get_color_of_link_by_name(start_label_name, end_label_name);
    const material = new THREE.MeshBasicMaterial({ color: preset_color ?? 0x0, transparent: true });

    const geometry = new THREE.CylinderGeometry(
      thickness * ConstantsOpenPose.BONE_THICKNESS_MULTIPLIER,
      thickness * ConstantsOpenPose.BONE_THICKNESS_MULTIPLIER,
      distance,
      8,
    );

    mesh = new THREE.Mesh(geometry, material);
    mesh.name = link_name;
    start_object.add(mesh);
  } else if (mesh) {
    const old_geometry = mesh.geometry;
    const geometry = new THREE.CylinderGeometry(
      thickness * ConstantsOpenPose.BONE_THICKNESS_MULTIPLIER,
      thickness * ConstantsOpenPose.BONE_THICKNESS_MULTIPLIER,
      distance,
      8,
    );
    mesh.geometry = geometry;
    mesh.updateMatrixWorld(true);
    old_geometry.dispose();
  } else {
    // nothing to update
    return;
  }

  if (fixed_distance !== undefined) {
    // In fixed distance mode, we still place at midpoint but use fixed distance for geometry
    // This allows visual representation to match the fixed length constraint
    const direction_local = position_end_local.clone().sub(position_start_local).normalize();
    
    // Adjust the positioning to reflect the fixed distance
    const fixed_end_local = position_start_local.clone().add(direction_local.multiplyScalar(fixed_distance));
    const midpoint_local = position_start_local.clone().add(fixed_end_local).multiplyScalar(0.5);
    mesh.position.copy(midpoint_local);
    
    // Orient cylinder from +Y to the direction vector
    const up_local = new THREE.Vector3(0, 1, 0);
    const dot = up_local.dot(direction_local);
    if (Math.abs(dot) < 0.9999) {
      const axis = up_local.clone().cross(direction_local).normalize();
      const angle = Math.acos(Math.max(-1, Math.min(1, up_local.dot(direction_local))));
      mesh.setRotationFromAxisAngle(axis, angle);
    } else {
      direction_local.y < 0 ? mesh.rotation.set(Math.PI, 0, 0) : mesh.rotation.set(0, 0, 0);
    }
  } else {
    // Standard positioning based on actual positions
    const midpoint_local = position_start_local.clone().add(position_end_local).multiplyScalar(0.5);
    mesh.position.copy(midpoint_local);

    // orient cylinder from +Y to the direction vector
    const direction_local = position_end_local.clone().sub(position_start_local).normalize();
    const up_local = new THREE.Vector3(0, 1, 0);

    const dot = up_local.dot(direction_local);
    if (Math.abs(dot) < 0.9999) {
      const axis = up_local.clone().cross(direction_local).normalize();
      const angle = Math.acos(Math.max(-1, Math.min(1, up_local.dot(direction_local))));
      mesh.setRotationFromAxisAngle(axis, angle);
    } else {
      direction_local.y < 0 ? mesh.rotation.set(Math.PI, 0, 0) : mesh.rotation.set(0, 0, 0);
    }
  }

  // update joint spheres on both ends
  update_joint_sphere(start_object, thickness * 2);
  update_joint_sphere(end_object, thickness * 2);
}

export function create_sphere(name: string, thickness: number, color: number) {
  const sphere = new THREE.Mesh(new THREE.SphereGeometry(thickness), new THREE.MeshBasicMaterial({ color }));
  sphere.name = name;

  return sphere;
}

export function create_group(name: string, x = 0, y = 0, z = 0) {
  const object = new THREE.Group();
  object.name = name;

  object.translateX(x);
  object.translateY(y);
  object.translateZ(z);

  return object;
}

export function create_joint(name: string, x = 0, y = 0, z = 0, thickness: number = ConstantsOpenPose.BONE_THICKNESS) {
  return create_group(name, x, y, z).add(create_sphere(name + ConstantsOpenPose.JOINT_SPHERE_NAME, thickness, get_preset_color_of_joint(name)));
}

export function is_virtual_point(name: string) {
  let none_virtual_items: string[] = [
    EOpenPoseBodyGroup.SHOULDER_RIGHT_INNER,
    EOpenPoseBodyGroup.SHOULDER_LEFT_INNER,
    EOpenPoseBodyGroup.HIP_RIGHT_INNER,
    EOpenPoseBodyGroup.HIP_LEFT_INNER,
  ];

  return none_virtual_items.includes(name);
}

export function create_hand(name: EOPBN.HAND_RIGHT | EOPBN.HAND_LEFT) {
  if (name === EOPBN.HAND_RIGHT) {
    const hand_right = SkeletonUtils.clone(ConstantsOpenPose.OPEN_POSE_PART_FROM_FILE_HAND);

    hand_right.name = EOPBN.HAND_RIGHT;
    hand_right.translateX(-0.4);
    hand_right.translateY(3);
    hand_right.rotateY(Math.PI);
    hand_right.rotateZ(-Math.PI / 2);
    hand_right.scale.multiplyScalar(ConstantsOpenPose.HAND_SCALE);

    hand_right.traverse(object => {
      if (!is_bone(object.name)) return;

      object.name = object.name + '_R';
    });

    return hand_right;
  } else {
    const hand_left = SkeletonUtils.clone(ConstantsOpenPose.OPEN_POSE_PART_FROM_FILE_HAND);

    hand_left.name = EOPBN.HAND_LEFT;
    hand_left.scale.x = -1;
    hand_left.translateX(0.4);
    hand_left.translateY(3);
    hand_left.rotateY(Math.PI);
    hand_left.rotateZ(Math.PI / 2);
    hand_left.scale.multiplyScalar(ConstantsOpenPose.HAND_SCALE);

    hand_left.traverse(object => {
      if (!is_bone(object.name)) return;

      object.name = object.name + '_L';
    });
    return hand_left;
  }
}

export function create_foot(name: EOPBN.FOOT_RIGHT | EOPBN.FOOT_LEFT) {
  if (name === EOPBN.FOOT_RIGHT) {
    const foot_right = SkeletonUtils.clone(ConstantsOpenPose.OPEN_POSE_PART_FROM_FILE_FOOT);

    foot_right.name = EOPBN.FOOT_RIGHT;
    foot_right.scale.setX(-1);
    foot_right.scale.multiplyScalar(ConstantsOpenPose.FOOT_SCALE);

    foot_right.traverse(object => {
      if (!is_bone(object.name)) return;

      object.name = object.name + '_R';
    });

    return foot_right;
  } else {
    const foot_left = SkeletonUtils.clone(ConstantsOpenPose.OPEN_POSE_PART_FROM_FILE_FOOT);
    foot_left.name = EOPBN.FOOT_LEFT;
    foot_left.scale.multiplyScalar(ConstantsOpenPose.FOOT_SCALE);

    foot_left.traverse(object => {
      if (!is_bone(object.name)) return;

      object.name = object.name + '_L';
    });
    return foot_left;
  }
}

export function is_need_save_object(name: string) {
  if (ConstantsOpenPose.OPEN_POSE_KEYPOINTS_LIST.includes(name)) return true;

  if (name === EOPBN.HAND_RIGHT || name === EOPBN.HAND_LEFT) return true;
  if (name === EOPBN.FOOT_RIGHT || name === EOPBN.FOOT_LEFT) return true;

  if (is_virtual_point(name)) return true; // virtual point

  if (is_bone(name)) return true;

  if (name.includes(ConstantsOpenPose.JOINT_SPHERE_NAME)) return true;
  if (name.includes(ConstantsOpenPose.LINK_NAME)) return true;

  return false;
}

export function find_object_item<T extends THREE.Object3D>(object: THREE.Object3D, name: string): T | null {
  let result = null;

  object.traverse(child => {
    if (child.name !== name) return;

    result = child;
  });

  return result;
}

export function get_connected_node_for_hand_foot(part_name: OpenPoseLimbs): EOPBN {
  let out_map = new Map([
    [EOPBN.HAND_LEFT, EOPBN.WRIST_LEFT],
    [EOPBN.HAND_RIGHT, EOPBN.WRIST_RIGHT],
    [EOPBN.FOOT_LEFT, EOPBN.ANKLE_LEFT],
    [EOPBN.FOOT_RIGHT, EOPBN.ANKLE_RIGHT],
  ]);

  let out = out_map.get_or_assert(part_name);

  return out;
}

// Helper function to get all connected bone names for a node
export function get_connected_bone_names_for_node(node_name: string): string[] {
  const connections: Record<string, string[]> = {
    [EOPBN.SHOULDER_LEFT]: [
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.SHOULDER_LEFT}`,
      `${EOPBN.SHOULDER_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ELBOW_LEFT}`,
    ],
    [EOPBN.SHOULDER_RIGHT]: [
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.SHOULDER_RIGHT}`,
      `${EOPBN.SHOULDER_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ELBOW_RIGHT}`,
    ],
    [EOPBN.ELBOW_LEFT]: [
      `${EOPBN.SHOULDER_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ELBOW_LEFT}`,
      `${EOPBN.ELBOW_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.WRIST_LEFT}`,
    ],
    [EOPBN.ELBOW_RIGHT]: [
      `${EOPBN.SHOULDER_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ELBOW_RIGHT}`,
      `${EOPBN.ELBOW_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.WRIST_RIGHT}`,
    ],
    [EOPBN.WRIST_LEFT]: [`${EOPBN.ELBOW_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.WRIST_LEFT}`],
    [EOPBN.WRIST_RIGHT]: [`${EOPBN.ELBOW_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.WRIST_RIGHT}`],
    [EOPBN.HIP_LEFT]: [
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.HIP_LEFT}`,
      `${EOPBN.HIP_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.KNEE_LEFT}`
    ],
    [EOPBN.HIP_RIGHT]: [
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.HIP_RIGHT}`,
      `${EOPBN.HIP_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.KNEE_RIGHT}`
    ],
    [EOPBN.KNEE_LEFT]: [
      `${EOPBN.HIP_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.KNEE_LEFT}`,
      `${EOPBN.KNEE_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ANKLE_LEFT}`,
    ],
    [EOPBN.KNEE_RIGHT]: [
      `${EOPBN.HIP_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.KNEE_RIGHT}`,
      `${EOPBN.KNEE_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ANKLE_RIGHT}`,
    ],
    [EOPBN.ANKLE_LEFT]: [`${EOPBN.KNEE_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ANKLE_LEFT}`],
    [EOPBN.ANKLE_RIGHT]: [`${EOPBN.KNEE_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ANKLE_RIGHT}`],
    [EOPBN.NECK]: [
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.NOSE}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.SHOULDER_LEFT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.SHOULDER_RIGHT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.EYE_LEFT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.EYE_RIGHT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.EAR_LEFT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.EAR_RIGHT}`,
    ],
    [EOPBN.NOSE]: [
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.NOSE}`,
      `${EOPBN.NOSE}${ConstantsOpenPose.LINK_NAME}${EOPBN.EYE_LEFT}`,
      `${EOPBN.NOSE}${ConstantsOpenPose.LINK_NAME}${EOPBN.EYE_RIGHT}`
    ],
    [EOPBN.EYE_LEFT]: [
      `${EOPBN.NOSE}${ConstantsOpenPose.LINK_NAME}${EOPBN.EYE_LEFT}`,
      `${EOPBN.EYE_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.EAR_LEFT}`
    ],
    [EOPBN.EYE_RIGHT]: [
      `${EOPBN.NOSE}${ConstantsOpenPose.LINK_NAME}${EOPBN.EYE_RIGHT}`,
      `${EOPBN.EYE_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.EAR_RIGHT}`
    ],
    [EOPBN.EAR_LEFT]: [`${EOPBN.EYE_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.EAR_LEFT}`],
    [EOPBN.EAR_RIGHT]: [`${EOPBN.EYE_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.EAR_RIGHT}`],
    [EOpenPoseBodyGroup.TORSO]: [
      // Torso connects to all bones
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.NOSE}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.SHOULDER_LEFT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.SHOULDER_RIGHT}`,
      `${EOPBN.SHOULDER_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ELBOW_LEFT}`,
      `${EOPBN.SHOULDER_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ELBOW_RIGHT}`,
      `${EOPBN.ELBOW_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.WRIST_LEFT}`,
      `${EOPBN.ELBOW_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.WRIST_RIGHT}`,
      `${EOPBN.HIP_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.KNEE_LEFT}`,
      `${EOPBN.HIP_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.KNEE_RIGHT}`,
      `${EOPBN.KNEE_LEFT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ANKLE_LEFT}`,
      `${EOPBN.KNEE_RIGHT}${ConstantsOpenPose.LINK_NAME}${EOPBN.ANKLE_RIGHT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.EYE_LEFT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.EYE_RIGHT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.EAR_LEFT}`,
      `${EOPBN.NECK}${ConstantsOpenPose.LINK_NAME}${EOPBN.EAR_RIGHT}`,
    ],
  };

  return connections[node_name] || [];
}

export function to_hex_color([r, g, b]: readonly [number, number, number]): number {
  return (r << 16) + (g << 8) + b;
}

function search_color(start: number, end: number): number | null {
  const index = ConstantsOpenPose.CONNECT_KEYPOINTS.findIndex(([s, e]) => s === start && e === end);

  if (index < 0 || index >= ConstantsOpenPose.CONNECTS_COLORS.length) return null;

  const [r, g, b] = ConstantsOpenPose.CONNECTS_COLORS[index];
  return (r << 16) + (g << 8) + b;
}

export function get_color_of_link_by_name(start_name: string, end_name: string): number | null {
  if (!start_name || !end_name) return null;

  const start_index = ConstantsOpenPose.OPEN_POSE_KEYPOINTS_LIST.indexOf(start_name);
  const end_index = ConstantsOpenPose.OPEN_POSE_KEYPOINTS_LIST.indexOf(end_name);

  if (start_index === -1 || end_index === -1) return null;

  if (start_index > end_index) {
    return search_color(end_index, start_index);
  } else {
    return search_color(start_index, end_index);
  }
}

export function update_bone_lengths_for_node_updated(node_info: OpenPoseSelectedNodeInfo, open_pose_body: OpenPoseBodyController): void {
  const { node, node_name } = node_info;

  if (!open_pose_body.has_node(node_name)) return;

  const world_position_current = open_pose_body.get_world_position(node);

  switch (node_name) {
    case EOPBN.SHOULDER_LEFT:
      update_shoulder_bone_length_individual_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.SHOULDER_RIGHT:
      update_shoulder_bone_length_individual_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.ELBOW_LEFT:
      update_elbow_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.ELBOW_RIGHT:
      update_elbow_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.WRIST_LEFT:
      update_wrist_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.WRIST_RIGHT:
      update_wrist_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.HIP_LEFT:
      update_hip_bone_length_individual_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.HIP_RIGHT:
      update_hip_bone_length_individual_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.KNEE_LEFT:
      update_knee_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.KNEE_RIGHT:
      update_knee_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.ANKLE_LEFT:
      update_ankle_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;

    case EOPBN.ANKLE_RIGHT:
      update_ankle_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;

    default:
      update_generic_bone_length_updated(node_name, world_position_current, open_pose_body);
      break;
  }
}

export function update_shoulder_bone_length_individual_updated(
  node_name: string,
  position_current: THREE.Vector3,
  open_pose_body: OpenPoseBodyController,
): void {
  const is_left = node_name.includes(EOpenPoseBodySide.LEFT);

  const neck_node = open_pose_body.get_node(EOPBN.NECK);

  const neck_pos = open_pose_body.get_world_position(neck_node.object);
  const shoulder_distance = neck_pos.distanceTo(position_current);

  const shoulder_bone_name = is_left ? EOpenPoseBodyBone.SHOULDER_LEFT : EOpenPoseBodyBone.SHOULDER_RIGHT;

  const shoulder_bone = open_pose_body.get_bone(shoulder_bone_name);

  shoulder_bone.set_distance(shoulder_distance);
}

export function update_hip_bone_length_individual_updated(node_name: string, position_current: THREE.Vector3, open_pose_body: OpenPoseBodyController): void {
  const is_left = node_name.includes(EOpenPoseBodySide.LEFT);

  const five_group = open_pose_body.get_group(EOPBN.NECK);
  const five_pos = open_pose_body.get_world_position(five_group);
  const hip_distance = five_pos.distanceTo(position_current);

  const hip_bone_name = is_left ? EOpenPoseBodyBone.THIGH_LEFT : EOpenPoseBodyBone.THIGH_RIGHT;

  const hip_bone = open_pose_body.get_bone(hip_bone_name);
  hip_bone.set_distance(hip_distance);
}

export function update_elbow_bone_length_updated(node_name: string, position_current: THREE.Vector3, open_pose_body: OpenPoseBodyController): void {
  const is_left = node_name.includes(EOpenPoseBodySide.LEFT);

  const shoulder_name = is_left ? EOPBN.SHOULDER_LEFT : EOPBN.SHOULDER_RIGHT;
  const shoulder_node = open_pose_body.get_node(shoulder_name);

  const shoulder_position = open_pose_body.get_world_position(shoulder_node.object);
  const upper_arm_length = shoulder_position.distanceTo(position_current);

  const upper_arm_bone_name = is_left ? EOpenPoseBodyBone.UPPER_ARM_LEFT : EOpenPoseBodyBone.UPPER_ARM_RIGHT;

  const upper_arm_bone = open_pose_body.get_bone(upper_arm_bone_name);
  upper_arm_bone.set_distance(upper_arm_length);
}

export function update_wrist_bone_length_updated(node_name: string, position_current: THREE.Vector3, open_pose_body: OpenPoseBodyController): void {
  const is_left = node_name.includes(EOpenPoseBodySide.LEFT);

  const elbow_name = is_left ? EOPBN.ELBOW_LEFT : EOPBN.ELBOW_RIGHT;
  const elbow_node = open_pose_body.get_node(elbow_name);

  const elbow_pos = open_pose_body.get_world_position(elbow_node.object);
  const forearm_length = elbow_pos.distanceTo(position_current);

  const forearm_bone_name = is_left ? EOpenPoseBodyBone.FOREARM_LEFT : EOpenPoseBodyBone.FOREARM_RIGHT;

  const forearm_bone = open_pose_body.get_bone(forearm_bone_name);
  forearm_bone.set_distance(forearm_length);
}

export function update_knee_bone_length_updated(node_name: string, position_current: THREE.Vector3, open_pose_body: OpenPoseBodyController): void {
  const is_left = node_name.includes(EOpenPoseBodySide.LEFT);

  const hip_name = is_left ? EOPBN.HIP_LEFT : EOPBN.HIP_RIGHT;
  const hip_node = open_pose_body.get_node(hip_name);

  const hip_pos = open_pose_body.get_world_position(hip_node.object);
  const thigh_length = hip_pos.distanceTo(position_current);

  const upper_leg_bone_name = is_left ? EOpenPoseBodyBone.UPPER_LEG_LEFT : EOpenPoseBodyBone.UPPER_LEG_RIGHT;

  const upper_leg_bone = open_pose_body.get_bone(upper_leg_bone_name);
  upper_leg_bone.set_distance(thigh_length);
}

export function update_ankle_bone_length_updated(node_name: string, position_current: THREE.Vector3, open_pose_body: OpenPoseBodyController): void {
  const is_left = node_name.includes(EOpenPoseBodySide.LEFT);

  const knee_name = is_left ? EOPBN.KNEE_LEFT : EOPBN.KNEE_RIGHT;
  const knee_node = open_pose_body.get_node(knee_name);

  const knee_position = open_pose_body.get_world_position(knee_node.object);
  const lower_leg_length = knee_position.distanceTo(position_current);

  const lower_leg_bone_name = is_left ? EOpenPoseBodyBone.LOWER_LEG_LEFT : EOpenPoseBodyBone.LOWER_LEG_RIGHT;

  const lower_leg_bone = open_pose_body.get_bone(lower_leg_bone_name);
  lower_leg_bone.set_distance(lower_leg_length);
}

export function update_generic_bone_length_updated(node_name: string, position_current: THREE.Vector3, open_pose_body: OpenPoseBodyController): void {
  const node = open_pose_body.get_node(node_name);

  if (!node.object.parent) return;
  if (!is_pickable(node.object.parent.name)) return;

  const position_local = node.object.parent.worldToLocal(position_current.clone());
  node.object.position.copy(position_local);

  const connected_bones = open_pose_body.get_bones_for_node(node_name);
  for (const bone of connected_bones) {
    bone.update_stored_distance();
  }
}

export function apply_position_constraints_to_vector(
  vector: THREE.Vector3,
  bounds: { x: { min: number; max: number }; y: { min: number; max: number }; z: { min: number; max: number } },
) {
  vector.x = Math.max(bounds.x.min, Math.min(bounds.x.max, vector.x));
  vector.y = Math.max(bounds.y.min, Math.min(bounds.y.max, vector.y));
  vector.z = Math.max(bounds.z.min, Math.min(bounds.z.max, vector.z));
}

export function apply_position_constraints(
  object_3d: Object3D,
  bounds: { x: { min: number; max: number }; y: { min: number; max: number }; z: { min: number; max: number } },
) {
  const position = object_3d.position;

  position.x = Math.max(bounds.x.min, Math.min(bounds.x.max, position.x));
  position.y = Math.max(bounds.y.min, Math.min(bounds.y.max, position.y));
  position.z = Math.max(bounds.z.min, Math.min(bounds.z.max, position.z));
}

export function set_color(bone: THREE.Bone, color = 0xff0000) {
  const point = bone.children.find(object => object instanceof THREE.Mesh && !is_mask(object.name)) as THREE.Mesh;

  const material = point.material as THREE.MeshBasicMaterial;
  material.color.set(color);
  material.needsUpdate = true;
}

export function get_movement_bounds(height: number) {
  return {
    x: { min: ConstantsOpenPose.BODY_MOVEMENT_BOUNDS.min_x, max: ConstantsOpenPose.BODY_MOVEMENT_BOUNDS.max_x },
    y: { min: ConstantsOpenPose.BODY_MOVEMENT_BOUNDS.min_y, max: height },
    z: { min: ConstantsOpenPose.BODY_MOVEMENT_BOUNDS.min_z, max: ConstantsOpenPose.BODY_MOVEMENT_BOUNDS.max_z },
  };
}

export function get_parent_nodes(node: Object3D): Object3D[] {
  const out: Object3D[] = [];
  let current = node.parent;

  while (current && current.name !== EOpenPoseBodyGroup.TORSO) {
    if (is_pickable(current.name)) out.push(current);
    current = current.parent;
  }

  return out;
}

export function set_full_opacity_for_body_links_and_joints(body: THREE.Object3D, opacity = 1.0) {
  body.traverse(child => {
    if (!(child instanceof THREE.Mesh)) return;
    if (!child.name.includes(ConstantsOpenPose.LINK_NAME) && !child.name.includes(ConstantsOpenPose.JOINT_SPHERE_NAME)) return;

    const material = child.material as THREE.MeshBasicMaterial;
    material.opacity = opacity;
    material.transparent = true;
    material.needsUpdate = true;
  });
}

export function get_bodies(scene: THREE.Scene) {
  return scene.children.filter(object => object.name === EOpenPoseBodyGroup.TORSO);
}

export function get_first_body_in_scene(scene: THREE.Scene): Object3D {
  const bodies = get_bodies(scene);

  return bodies.length > 0 ? bodies[0] : EThreejsStub.OBJECT;
}

export function get_material_first(mesh: THREE.Mesh): THREE.Material {
  let out: THREE.Material = EThreejsStub.MATERIAL;

  const materials = mesh.material;

  if (Array.isArray(materials)) {
    for (const material of materials) {
      if (material instanceof THREE.Material) out = material;
    }
  } else if (materials instanceof THREE.Material) {
    out = materials;
  }

  return out;
}
